import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle,
  AttachFile,
  ArrowBack,
  Send,
} from "@mui/icons-material";
import axios from "axios";
import { ACCREDITEDKYCWEBSERVICE, showToast } from "../../CoreDataService";
import {
  ACCREDITATION_TYPES,
  CERTIFICATION_TYPES,
  ENTITY_TYPES,
  CURRENCY_OPTIONS,
  OnlyAllowedNumbers,
  allowOnlyAlphabets,
  formatCurrency,
  allowAlphanumeric,
  allowNumericOnly,
} from "./utils";


const AccreditedReview = ({
  handleBack,
  userDetails,
  formData,
  setIsLoading,
  userId,
  onSubmitSuccess,
  applicationId,
  isEditing,
  isRejected,
  theme,
  handleFormDataUpdate
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [applicationData, setApplicationData] = useState(null);
  const [loadingApplication, setLoadingApplication] = useState(false);

  // Add state for resubmission self-declaration
  const [resubmitSelfDeclaration, setResubmitSelfDeclaration] = useState(false);

  const homeCurrencySymbol = localStorage.getItem("homeCurrencySymbol") || "$";
  const homeCurrency = localStorage.getItem("homeCurrency") || "USD";

  // Dark mode colors
  const darkColors = {
    background: "#121212",
    paper: "#1e1e1e",
    card: "#1a1a1a",
    text: "#e0e0e0",
    textSecondary: "#aaa",
    border: "#333",
    primary: "#0d6efd",
    success: "#28a745",
    info: "#0dcaf0",
    warning: "#ffc107",
    error: "#dc3545",
  };

  // Light mode colors
  const lightColors = {
    background: "#f8f9fa",
    paper: "#ffffff",
    card: "#f8f9fa",
    text: "#212529",
    textSecondary: "#6c757d",
    border: "#dee2e6",
    primary: "#1976d2",
    success: "#2e7d32",
    info: "#0288d1",
    warning: "#ed6c02",
    error: "#d32f2f",
  };

  const colors = theme === "dark" ? darkColors : lightColors;

  // Fetch application data when component mounts (for rejected/editing cases)
  useEffect(() => {
    if (applicationId && (isRejected || isEditing)) {
      fetchApplicationData();
    }
  }, [applicationId, isRejected, isEditing]);

  const fetchApplicationData = async () => {
    setLoadingApplication(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${ACCREDITEDKYCWEBSERVICE}/accreditation/application/${applicationId}`,
        {
          headers: {
            Authorization: `BEARER ${token}`,
          },
        }
      );

      if (response?.data?.success) {
        setApplicationData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching application data:", error);
      showToast("error", "Failed to load application data");
    } finally {
      setLoadingApplication(false);
    }
  };

  const buildPayload = () => {
    const accreditationType = formData.accreditationType || applicationData?.accreditationType;

    // Base payload - common for all types
    const basePayload = {
      userId: userId,
      accreditationType: accreditationType,
      verificationMethod: formData.verificationMethod || applicationData?.verificationMethod || "DOCUMENT_UPLOAD",
      selfDeclaration: true, // Always send as true boolean
    };

    // Build type-specific payload
    switch (accreditationType) {
      case "INCOME":
        return {
          ...basePayload,
          annualIncome: parseFloat(formData.annualIncome || applicationData?.annualIncome || 0),
          jointIncome: formData.jointIncome ? parseFloat(formData.jointIncome) : (applicationData?.jointIncome ? parseFloat(applicationData.jointIncome) : null),
          incomeCurrency: formData.incomeCurrency || applicationData?.incomeCurrency || "USD",
        };

      case "NET_WORTH":
        return {
          ...basePayload,
          netWorth: parseFloat(formData.netWorth || applicationData?.netWorth || 0),
          netWorthCurrency: formData.netWorthCurrency || applicationData?.netWorthCurrency || "USD",
        };

      case "PROFESSIONAL_CERTIFICATION":
        return {
          ...basePayload,
          certificationType: formData.certificationType || applicationData?.certificationType,
          certificationNumber: formData.certificationNumber || applicationData?.certificationNumber,
          certificationIssuer: formData.certificationIssuer || applicationData?.certificationIssuer,
          certificationExpiry: formData.certificationExpiry || applicationData?.certificationExpiry,
        };

      case "ENTITY":
        return {
          ...basePayload,
          entityName: formData.entityName || applicationData?.entityName,
          entityType: formData.entityType || applicationData?.entityType,
          entityRegistrationNo: formData.entityRegistrationNo || applicationData?.entityRegistrationNo,
          entityJurisdiction: formData.entityJurisdiction || applicationData?.entityJurisdiction,
          entityAssets: formData.entityAssets ? parseFloat(formData.entityAssets) : (applicationData?.entityAssets ? parseFloat(applicationData.entityAssets) : null),
        };

      default:
        // Fallback - include all possible fields
        return {
          ...basePayload,
          annualIncome: formData.annualIncome ? parseFloat(formData.annualIncome) : null,
          incomeCurrency: formData.incomeCurrency || "USD",
        };
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setIsLoading(true);

    try {
      const token = localStorage.getItem("access_token");

      // Validate required fields
      if (!formData.accreditationType && !applicationData?.accreditationType) {
        throw new Error("Accreditation type is required");
      }

      // IMPORTANT: For resubmission, check the resubmitSelfDeclaration state
      // For new applications, check formData.selfDeclaration
      if (isRejected) {
        if (!resubmitSelfDeclaration) {
          throw new Error("Please confirm the self-declaration to resubmit");
        }
      } else {
        const selfDeclarationValue = formData.selfDeclaration === true ||
          formData.selfDeclaration === "true" ||
          formData.selfDeclaration === 1 ||
          formData.selfDeclaration === "1";

        if (!selfDeclarationValue) {
          throw new Error("Please confirm the self-declaration");
        }
      }

      // Build the appropriate payload based on accreditation type
      const payload = buildPayload();

      let response;

      // For rejected applications, use the resubmit API
      if (isRejected && applicationId) {
        response = await axios.put(
          `${ACCREDITEDKYCWEBSERVICE}/accreditation/application/${applicationId}/resubmit`,
          payload,
          {
            headers: {
              Authorization: `BEARER ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      }
      // For new applications or other statuses
      else {
        const targetApplicationId = applicationId || "";
        response = await axios.post(
          `${ACCREDITEDKYCWEBSERVICE}/accreditation/application/${targetApplicationId}/submit?userId=${userId}`,
          payload,
          {
            headers: {
              Authorization: `BEARER ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (response?.data?.success) {
        showToast("success", isRejected
          ? "Application resubmitted successfully"
          : "Application submitted successfully"
        );
        onSubmitSuccess();
      } else {
        setSubmitError(response?.data?.message || "Submission failed");
        showToast("error", response?.data?.message || "Submission failed");
      }
    } catch (error) {
      console.error("Submission error:", error);
      const errorMsg =
        error?.response?.data?.message ||
        error.message ||
        "Failed to submit application";

      setSubmitError(errorMsg);
      showToast("error", errorMsg);
    } finally {
      setSubmitting(false);
      setIsLoading(false);
    }
  };

  // Format currency
 /*  const formatCurrency = (value, currency = "USD") => {
    if (!value) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }; */

  // Get accreditation type label
  const getAccreditationTypeLabel = (type) => {
    const types = {
      INCOME: "Income-Based",
      NET_WORTH: "Net Worth-Based",
      PROFESSIONAL_CERTIFICATION: "Professional Certification",
      ENTITY: "Entity-Based",
    };
    return types[type] || type;
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    const accreditationType = formData.accreditationType || applicationData?.accreditationType;

    // For resubmission, check the resubmitSelfDeclaration state
    if (isRejected) {
      return accreditationType && resubmitSelfDeclaration;
    }

    // For new applications, check formData.selfDeclaration
    const selfDeclarationValue = formData.selfDeclaration === true ||
      formData.selfDeclaration === "true" ||
      formData.selfDeclaration === 1 ||
      formData.selfDeclaration === "1";

    return accreditationType && selfDeclarationValue;
  };

  // Get missing fields for error display
  const getMissingFields = () => {
    const missing = [];
    if (!formData.accreditationType && !applicationData?.accreditationType) {
      missing.push("Accreditation Type");
    }

    // For resubmission, check the resubmitSelfDeclaration state
    if (isRejected) {
      if (!resubmitSelfDeclaration) missing.push("Self Declaration");
    } else {
      const selfDeclarationValue = formData.selfDeclaration === true ||
        formData.selfDeclaration === "true" ||
        formData.selfDeclaration === 1 ||
        formData.selfDeclaration === "1";

      if (!selfDeclarationValue) missing.push("Self Declaration");
    }

    return missing;
  };

  // Check if self declaration is confirmed (for display in non-resubmission mode)
  const isSelfDeclarationConfirmed = () => {
    return formData.selfDeclaration === true ||
      formData.selfDeclaration === "true" ||
      formData.selfDeclaration === 1 ||
      formData.selfDeclaration === "1";
  };

  // Get display data (prefer formData, fallback to applicationData)
  const getDisplayValue = (formDataKey, applicationDataKey) => {
    return formData[formDataKey] || applicationData?.[applicationDataKey || formDataKey];
  };

  if (loadingApplication) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
        <CircularProgress sx={{ color: colors.primary }} />
        <Typography sx={{ ml: 2, color: colors.text }}>Loading application data...</Typography>
      </Box>
    );
  }

  const displayAccreditationType = getDisplayValue('accreditationType', 'accreditationType');

  return (
    <Box sx={{ mt: 2 }}>
      {/* Rejection-specific banner */}
      {isRejected && (
        <Alert
          severity="info"
          sx={{
            mb: 3,
            backgroundColor: colors.info + '20',
            border: `1px solid ${colors.info}40`
          }}
        >
          <strong>Resubmitting Application</strong>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You are resubmitting your application after rejection. Please ensure all documents are updated and accurate.
          </Typography>
        </Alert>
      )}

      {/* Summary Card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{ color: colors.text, mb: 2, fontWeight: 600 }}
        >
          Application Summary
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Applicant Name
            </Typography>
            <Typography variant="body1" sx={{ color: colors.text, fontWeight: 500 }}>
              {userDetails?.first_name} {userDetails?.last_name}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Application Type
            </Typography>
            <Chip
              label={getAccreditationTypeLabel(displayAccreditationType)}
              size="small"
              sx={{
                mt: 0.5,
                backgroundColor: colors.primary,
                color: "#fff",
                fontWeight: 500,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1, borderColor: colors.border }} />
          </Grid>

          {/* Type-specific fields */}
          {displayAccreditationType === "INCOME" && (
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Annual Income
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text, fontWeight: 500 }}>
                  {formatCurrency(
                    getDisplayValue('annualIncome', 'annualIncome'),
                    homeCurrency
                  )}
                </Typography>
              </Grid>
              {getDisplayValue('jointIncome', 'jointIncome') && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    Joint Income
                  </Typography>
                  <Typography variant="body1" sx={{ color: colors.text, fontWeight: 500 }}>
                    {formatCurrency(
                      getDisplayValue('jointIncome', 'jointIncome'),
                      homeCurrency
                    )}
                  </Typography>
                </Grid>
              )}
            </>
          )}

          {displayAccreditationType === "NET_WORTH" && (
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Net Worth
              </Typography>
              <Typography variant="body1" sx={{ color: colors.text, fontWeight: 500 }}>
                {formatCurrency(
                  getDisplayValue('netWorth', 'netWorth'),
                  homeCurrency
                )}
              </Typography>
            </Grid>
          )}

          {displayAccreditationType === "PROFESSIONAL_CERTIFICATION" && (
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Certification Type
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text, fontWeight: 500 }}>
                  {getDisplayValue('certificationType', 'certificationType') || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Certification Number
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text, fontWeight: 500 }}>
                  {getDisplayValue('certificationNumber', 'certificationNumber') || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Certification Issuer
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text, fontWeight: 500 }}>
                  {getDisplayValue('certificationIssuer', 'certificationIssuer') || "N/A"}
                </Typography>
              </Grid>
            </>
          )}

          {displayAccreditationType === "ENTITY" && (
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Entity Name
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text, fontWeight: 500 }}>
                  {getDisplayValue('entityName', 'entityName') || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Entity Type
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text, fontWeight: 500 }}>
                  {getDisplayValue('entityType', 'entityType') || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Registration Number
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text, fontWeight: 500 }}>
                  {getDisplayValue('entityRegistrationNo', 'entityRegistrationNo') || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Jurisdiction
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text, fontWeight: 500 }}>
                  {getDisplayValue('entityJurisdiction', 'entityJurisdiction') || "N/A"}
                </Typography>
              </Grid>
              {getDisplayValue('entityAssets', 'entityAssets') && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    Entity Assets
                  </Typography>
                  <Typography variant="body1" sx={{ color: colors.text, fontWeight: 500 }}>
                    {formatCurrency(getDisplayValue('entityAssets', 'entityAssets'), homeCurrency)}
                  </Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>

        {/* Self Declaration Section - Always show checkbox */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: colors.card,
            borderRadius: 2,
            border: `1px solid ${colors.border}`,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isRejected ? resubmitSelfDeclaration : isSelfDeclarationConfirmed()}
                onChange={(e) => {
                  if (isRejected) {
                    setResubmitSelfDeclaration(e.target.checked);
                  } else {
                    // Update formData for new applications
                    handleFormDataUpdate({ selfDeclaration: e.target.checked });
                  }
                }}
                sx={{
                  color: colors.primary,
                  '&.Mui-checked': {
                    color: colors.primary,
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: colors.text, fontWeight: 600 }}
                >
                  Self Declaration Confirmation *
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  I confirm that all information provided is true and accurate. Knowingly providing false information may result in rejection of my application and possible account restrictions.
                </Typography>
              </Box>
            }
            sx={{
              alignItems: 'flex-start',
              mx: 0,
            }}
          />
        </Box>
      </Paper>

      {/* Documents Summary */}
      {(applicationData?.documents?.length > 0 || formData.documents?.length > 0) && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: colors.text, mb: 2, fontWeight: 600 }}
          >
            Uploaded Documents
          </Typography>
          <List disablePadding>
            {(applicationData?.documents || formData.documents || []).map((doc, index) => (
              <ListItem
                key={index}
                sx={{
                  px: 0,
                  borderBottom:
                    index < (applicationData?.documents || formData.documents || []).length - 1
                      ? `1px solid ${colors.border}`
                      : "none",
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <AttachFile sx={{ color: colors.primary, fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ color: colors.text }}>
                      {doc.documentName || doc.name || `Document ${index + 1}`}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                      {doc.documentSize ? `${(doc.documentSize / 1024).toFixed(2)} KB` :
                        doc.size ? `${(doc.size / 1024).toFixed(2)} KB` : ""}
                      {doc.documentType && ` • ${doc.documentType.replace(/_/g, ' ')}`}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Error Display */}
      {submitError && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            backgroundColor: colors.error + "20",
            color: colors.text,
          }}
        >
          {submitError}
        </Alert>
      )}

      {/* Validation Warning */}
      {!isFormValid() && (
        <Alert
          severity="warning"
          sx={{
            mb: 3,
            backgroundColor: colors.warning + "20",
            color: colors.text,
          }}
        >
          <strong>Missing Required Information</strong>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please complete the following before submitting:
          </Typography>
          <ul style={{ marginTop: 4, marginBottom: 0 }}>
            {getMissingFields().map((field, index) => (
              <li key={index}>
                <Typography variant="body2">{field}</Typography>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 4,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBack />}
          disabled={submitting}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            borderColor: colors.border,
            color: colors.textSecondary,
            "&:hover": {
              borderColor: colors.primary,
              backgroundColor: `${colors.primary}10`,
            },
          }}
        >
          Back
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !isFormValid()}
          startIcon={
            submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Send />
            )
          }
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1,
            backgroundColor: isRejected ? colors.warning : colors.primary,
            "&:hover": {
              backgroundColor: isRejected
                ? (theme === "dark" ? "#b45a1c" : "#e65100")
                : (theme === "dark" ? "#1565c0" : "#115293"),
            },
            "&:disabled": {
              backgroundColor: colors.border,
            },
          }}
        >
          {submitting
            ? "Submitting..."
            : isRejected
              ? "Resubmit Application"
              : isEditing
                ? "Update Application"
                : "Submit Application"}
        </Button>
      </Box>

      {/* Disclaimer */}
      <Typography
        variant="caption"
        sx={{
          color: colors.textSecondary,
          display: "block",
          mt: 3,
          textAlign: "center",
        }}
      >
        By submitting this application, you confirm that all information provided
        is true and accurate. Knowingly providing false information may result in
        rejection of your application and possible account restrictions.
      </Typography>
    </Box>
  );
};

export default AccreditedReview;